import * as React from "react";
import { connect } from "react-redux";
import { RootState, OrganizationsState } from "../../../../store/types";
import { OrgsSidebarStyle } from "./orgs-sidebar-style";
import { isUserData, UserDataAndState } from "common/Database";
import { Switch, RouteComponentProps, Route, withRouter, Redirect } from "react-router-dom";
import * as routes from "../../../../routes";
import * as actions from "../../../../actions/actions";
import { withDefault } from "collection-utils";
import MoreMenu from "../more-menu/more-menu";
import AppIcon from "common/app-icon";
import { isUserOrgID } from "../orgs-lib";
import { MappedThunk } from "../../../../actions/action-thunk";
import { getFeatureSetting } from "common/feature-settings";
import { PrivacySettingsModal } from "designer/modals/smart/privacy-settings-modal/privacy-settings-modal";
import { OrgTitleSidebarItem } from "./org-title-sidebar-item";
import { OrgSubtitleSidebarItem } from "./org-subtitle-sidebar-item";
import Img from "webapp/ui/smart/img/img";
import { withModalProvider } from "designer/modals-v2/modal-provider-v2/modal-provider-v2";
import CreateTeam, { CreateTeamModalStyle } from "designer/apps/ui/smart/new-team-flow/create-team";
import { SimpleModalContextProps } from "designer/modals-v2/modal-provider-v2/modal-context";
import { dontAwait } from "@glideapps/ts-necessities";
import WindowedModalV2 from "designer/modals-v2/windowed-modal/windowed-modal";
import { DragDropContext, Draggable, Droppable, DropResult } from "react-beautiful-dnd";
import { isUndefinedish, reorder } from "common/Support";
import * as userActions from "actions/user-actions";

interface RouteProps {
    orgID: string | undefined;
}

interface Props extends RouteComponentProps {
    section: "apps" | "members" | "billing" | "referrals" | "none";
    userState: UserDataAndState;
    organizations: OrganizationsState;
    proPlans: number;
    signOut: MappedThunk<typeof actions.signOut>;
    reorderUserOrgs: MappedThunk<typeof userActions.reorderUserOrgs>;
    readonly modals: SimpleModalContextProps;
}

const GlideSmallIcon = () => (
    <a href="https://www.glideapps.com" target="_blank" rel="noreferrer noopener">
        <svg
            className="logo-small"
            width="32"
            height="32"
            viewBox="0 0 32 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg">
            <path
                className="bg"
                d="M0 16C0 7.16344 7.16344 0 16 0V0C24.8366 0 32 7.16344 32 16V16C32 24.8366 24.8366 32 16 32V32C7.16344 32 0 24.8366 0 16V16Z"
                fill="currentColor"
            />
            <path
                className="fg"
                d="M16.1409 24.1509C18.5543 23.7107 20.0751 22.3747 20.6991 20.3789L20.9427 22.8752L23.0374 22.4932L22.9993 13.5133L15.9639 14.7965L15.9652 17.1253L20.3367 16.328L20.337 16.8455C20.342 18.4208 19.6024 21.0729 16.3237 21.6709C14.0014 22.0944 12.3014 21.1576 11.666 17.6741C11.0514 14.3044 11.6762 9.86173 15.729 9.12258C17.7781 8.74885 19.2586 9.51396 19.8911 11.563L22.7596 10.5222C21.8037 7.47357 19.1533 6.09844 15.4876 6.767C9.08969 7.93386 7.75149 13.7535 8.57369 18.2616C9.4831 23.2479 12.7256 24.7738 16.1409 24.1509Z"
                fill="currentColor"
            />
        </svg>
    </a>
);

class OrgsSidebarImpl extends React.PureComponent<Props & RouteProps> {
    private onAddTeam = async () => {
        await this.props.modals.showWindowedModal(
            (_, onComplete) => (
                <CreateTeamModalStyle>
                    <CreateTeam onComplete={onComplete} />
                </CreateTeamModalStyle>
            ),
            true
        );
    };
    private onAppsClick = (newOrgID: string) => {
        const { history, orgID, section } = this.props;
        if (section === "apps" && newOrgID === orgID) return;
        history.push(`/o/${newOrgID}`);
    };

    private onMembersClick = () => {
        const { history, orgID, section } = this.props;
        if (orgID === undefined || orgID === "0" || section === "members") return;

        history.push(`/o/${orgID}/members`);
    };

    private onBillingClick = (ev: React.MouseEvent) => {
        // We have to do this due to an unfortunate design characteristic of the orgs
        // sidebar. If you don't stop the propagation, then clicking on the "Billing"
        // section of the sidebar in My Apps from a non-Apps state just skips
        // "Billing" and goes straight back to "Apps".
        ev.stopPropagation();
        const { history, orgID, section } = this.props;
        if (section === "billing") return;

        history.push(`/o/${orgID ?? "0"}/billing`);
    };

    private onReferralClick = (ev: React.MouseEvent) => {
        ev.stopPropagation();
        const { history, section } = this.props;
        if (section === "referrals") return;

        history.push("/o/0/referrals");
    };

    private onPrivacySettingsClick = () => {
        dontAwait(
            this.props.modals.showModal<void>(resolve => {
                return (
                    <WindowedModalV2 canClose={true} hideClosingButton={true} onClose={resolve}>
                        <PrivacySettingsModal />
                    </WindowedModalV2>
                );
            })
        );
    };

    public render(): React.ReactNode {
        const { userState, organizations, orgID, section, signOut, reorderUserOrgs, location } = this.props;

        if (userState === null || userState === "loading") {
            return <OrgsSidebarStyle />;
        }

        const isUserApps = isUserOrgID(orgID);

        const menu: { content: string; onClick: () => void }[] = [];

        menu.push({
            content: "Privacy Settings",
            onClick: this.onPrivacySettingsClick,
        });

        menu.push({
            content: "Sign Out",
            onClick: signOut,
        });

        const hasMyApps = isUserData(userState) && (userState.appIDs.length > 0 || userState.orgUserIDs.length === 0);

        if (location.pathname.endsWith("/new") && !hasMyApps) {
            return <Redirect to={routes.newApp(userState.orgUserIDs[0])} />;
        } else if (!hasMyApps && isUserApps && section !== "none") {
            return <Redirect to={routes.org(userState.orgUserIDs[0])} />;
        }

        const Subnav = ({ orgID: navOrgID }: { orgID: string }) => {
            const org = organizations.find(o => o.id === orgID);
            return (
                <ul>
                    <OrgSubtitleSidebarItem
                        icon="apps"
                        label="Apps"
                        count={isUserOrgID(navOrgID) ? userState.appIDs.length : org?.appIDs.length}
                        active={section === "apps"}
                        onClick={() => this.onAppsClick(navOrgID)}
                    />

                    {!isUserApps && (
                        <OrgSubtitleSidebarItem
                            icon="members"
                            label="Members"
                            active={section === "members"}
                            onClick={this.onMembersClick}
                            count={isUserOrgID(navOrgID) ? undefined : org?.memberUserIDs.length}
                        />
                    )}

                    {getFeatureSetting("referralLinks") && isUserApps ? (
                        <OrgSubtitleSidebarItem
                            icon="members"
                            label="Referrals"
                            active={section === "referrals"}
                            onClick={this.onReferralClick}
                            count={userState.referrals}
                        />
                    ) : null}

                    <OrgSubtitleSidebarItem
                        icon="billing"
                        label="Billing"
                        active={section === "billing"}
                        onClick={this.onBillingClick}
                    />
                </ul>
            );
        };

        return (
            <OrgsSidebarStyle>
                <GlideSmallIcon />
                <ul>
                    {hasMyApps && (
                        <li onClick={() => this.onAppsClick("0")}>
                            <OrgTitleSidebarItem id={userState.id} label="My Apps" active={isUserApps} />
                            {isUserApps && <Subnav orgID="0" />}
                        </li>
                    )}
                    <DragDropContext
                        onDragEnd={async (result: DropResult) => {
                            if (isUndefinedish(result.source) || isUndefinedish(result.destination)) return;
                            const sourceOrg = organizations[result.source.index];
                            const reordered = reorder(organizations, result.source.index, result.destination.index);
                            const destOrg = reordered[result.destination.index - 1];
                            await reorderUserOrgs(sourceOrg, destOrg);
                        }}>
                        <Droppable droppableId="orgs">
                            {droppableProps => (
                                <div ref={droppableProps.innerRef} {...droppableProps.droppableProps}>
                                    {organizations.map((org, index) => {
                                        const isActive = org.id === orgID;
                                        return (
                                            <Draggable key={org.id} draggableId={org.id} index={index}>
                                                {draggableProps => (
                                                    <li
                                                        key={org.id}
                                                        ref={draggableProps.innerRef}
                                                        {...draggableProps.dragHandleProps}
                                                        {...draggableProps.draggableProps}>
                                                        <OrgTitleSidebarItem
                                                            id={org.id}
                                                            label={org.displayName}
                                                            active={isActive}
                                                            onClick={() => this.onAppsClick(org.id)}
                                                        />
                                                        {isActive && <Subnav orgID={org.id} />}
                                                    </li>
                                                )}
                                            </Draggable>
                                        );
                                    })}
                                    {droppableProps.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>
                    <li key="new team">
                        <div className="osb-header" onClick={this.onAddTeam}>
                            <div className="osb-btn-icon">
                                <AppIcon icon="plusStroke" size={16} />
                            </div>
                            <div>New Team…</div>
                        </div>
                    </li>
                </ul>
                <div className="osb-user-header">
                    <Img className="osb-user-photo" src={userState.photoURL} alt={userState.displayName} />
                    <span>{userState.displayName}</span>
                    <MoreMenu
                        data-cy="user-menu"
                        items={menu}
                        descriptionForItem={i => i.content}
                        onItemClick={i => i.onClick()}
                    />
                </div>
            </OrgsSidebarStyle>
        );
    }
}

const mapState = (state: RootState) => ({
    userState: state.userData,
    organizations: state.organizations,
    proPlans: state.userData !== null && state.userData !== "loading" ? state.userData.planCounts.pro : 0,
});

const mapDispatch = {
    signOut: actions.signOut,
    reorderUserOrgs: userActions.reorderUserOrgs,
};

const OrgsSidebarConnected = withModalProvider(withRouter(connect(mapState, mapDispatch)(OrgsSidebarImpl)));

const OrgsSidebar = () => {
    return (
        <Switch>
            <Route
                exact={true}
                path={routes.orgMembersPattern}
                render={(p: RouteComponentProps<RouteProps>) => (
                    <OrgsSidebarConnected section="members" orgID={withDefault(p.match.params.orgID, "0")} />
                )}
            />
            <Route
                exact={true}
                path={routes.orgBillingPattern}
                render={(p: RouteComponentProps<RouteProps>) => (
                    <OrgsSidebarConnected section="billing" orgID={withDefault(p.match.params.orgID, "0")} />
                )}
            />
            <Route
                exact={true}
                path={routes.orgReferralPattern}
                render={() => <OrgsSidebarConnected section="referrals" orgID="0" />}
            />
            <Route
                exact={true}
                path={[routes.overview, routes.orgPattern]}
                render={(p: RouteComponentProps<RouteProps>) => (
                    <OrgsSidebarConnected section="apps" orgID={withDefault(p.match.params.orgID, "0")} />
                )}
            />
            <Route
                render={(p: RouteComponentProps<RouteProps>) => (
                    <OrgsSidebarConnected section="none" orgID={withDefault(p.match.params.orgID, "0")} />
                )}
            />
        </Switch>
    );
};

export default OrgsSidebar;
